import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthLoadingScreen from "../../components/AuthLoadingScreen";
import authService from "../../services/authService";
import sessionAuthApi from "../../services/sessionAuth";
import { oauthRedirectUri, takeOAuthAttempt } from "../../services/googleOAuth";
import { extractRolesFromUser, needsWorkspaceOnboarding, postLoginPath } from "../../utils/authorization";

/** OAuth callback from Keycloak (Google). Verifies state, exchanges the code, then routes the user. */
const Callback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The code is single-use; StrictMode runs effects twice in development, so only the first run exchanges it.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const attempt = takeOAuthAttempt();
    const code = params.get("code");
    const state = params.get("state");
    if (params.get("error") || !code || !attempt || attempt.state !== state) {
      navigate("/login?error=oauth", { replace: true });
      return;
    }

    (async () => {
      try {
        const result = await sessionAuthApi.oauthExchange({
          code,
          codeVerifier: attempt.verifier,
          redirectUri: oauthRedirectUri(),
          inviteToken: attempt.invite,
          // Prefer creating the workspace in the same exchange so we never land on /home without roles.
          workspaceName: attempt.workspaceName,
        });
        if (result.onboardingRequired) {
          navigate("/onboarding/workspace", { replace: true });
          return;
        }
        let user = await authService.getUser();
        let roles = extractRolesFromUser(user);
        if (needsWorkspaceOnboarding(roles)) {
          // Workspace was already named before Google, or invite joined — refresh claims silently.
          // Do not ask for the workspace name a second time.
          if (attempt.workspaceName || attempt.invite) {
            try {
              await sessionAuthApi.completeWorkspaceOnboarding(
                attempt.workspaceName?.trim() || "workspace"
              );
              user = await authService.getUser();
              roles = extractRolesFromUser(user);
              if (!needsWorkspaceOnboarding(roles)) {
                navigate(postLoginPath(roles), { replace: true });
                return;
              }
            } catch {
              // fall through to onboarding / login
            }
          }
          navigate("/onboarding/workspace", { replace: true });
          return;
        }
        navigate(postLoginPath(roles), { replace: true });
      } catch {
        // Session may already be saved from a partial exchange; never dump a roleless user on /home.
        const user = await authService.getUser().catch(() => null);
        if (user && needsWorkspaceOnboarding(extractRolesFromUser(user))) {
          navigate("/onboarding/workspace", { replace: true });
          return;
        }
        navigate("/login?error=oauth", { replace: true });
      }
    })();
  }, [navigate, params]);

  return <AuthLoadingScreen message={t("auth.signingIn")} />;
};

export default Callback;
